import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DIMS = ['country', 'customer', 'species', 'process', 'form', 'grade'];
const KLAB = ['Country', 'Customer', 'Species', 'Process', 'Form', 'Grade'];
const ACC = ['#4FC3C9', '#FF6B52', '#F2B14C', '#8FD694', '#B98CD6', '#F08FA8'];
const COLQ = 7, COLV = 8; // aggregated-row indices: [dim0..dim5, supplierIdx, qty, val, cnt]

const fmtV = (v) => v >= 1e6 ? '$' + (v / 1e6).toFixed(2) + 'M' : (v >= 1e3 ? '$' + (v / 1e3).toFixed(0) + 'k' : '$' + v.toFixed(0));
const fmtQ = (k) => k >= 1000 ? (k / 1000).toFixed(1) + ' t' : k.toFixed(0) + ' kg';
// The shipment-details stat bar uses slightly different rounding than the column view (matches legacy markup exactly).
const fmtShipVal = (v) => v >= 1e6 ? '$' + (v / 1e6).toFixed(2) + 'M' : (v >= 1e3 ? '$' + (v / 1e3).toFixed(1) + 'K' : '$' + v.toFixed(0));

const GUIDE = {
  sections: [
    { title: '7-Column Cascade', desc: 'Click through: Country → Customer → Species → Process → Form → Grade → Supplier. Each click filters the next column to show only what matches your selection.', example: 'Example: Click "United States" → "AZ Gems Inc" → "Vannamei" → "Raw" → "PD (Tail-Off)" → "31/40" to see exactly which exporters supply this specific SKU.' },
    { title: 'Bar Width & Values', desc: "Each row shows a bar proportional to its value ($ or tonnes, based on toggle). The widest bar is the market leader for that column's context.", example: 'Example: In the US, PD (Tail-Off) at $1.95B is nearly 3x larger than EZ-Peel at $371M — showing clear product dominance.' },
    { title: 'Price Chart', desc: 'After selecting a supplier, the "Price per kg" chart shows their monthly pricing trend across all 23 months vs the market average (dashed line) for the same product.', example: "Example: If a supplier's orange line sits above the teal dashed market avg, they're pricing premium. Below = they're undercutting." },
    { title: 'Shipment Details', desc: 'Click a supplier to see individual shipment records for that supplier+product combination. Each row is an actual export transaction.', example: 'Example: See the exact date, quantity, and FOB value of each shipment from Devi Fisheries for 31/40 PD to the US.' },
  ],
  lookFor: "Drill into Sharat's own shipments to audit pricing consistency. Compare Sharat's price vs market avg — premium or discount? Check if competitors undercut on key SKUs.",
};

// Collapses the month dimension (row[0]) from the raw [mi, ci, cui, si, pi, fi, gi, sui, qty, val, cnt]
// rows into 10-col aggregated rows [ci, cui, si, pi, fi, gi, sui, qty, val, cnt] for a month range.
function aggregateMonths(fullRows, fromIdx, toIdx) {
  const g = new Map();
  for (const r of fullRows) {
    if (r[0] < fromIdx || r[0] > toIdx) continue;
    const key = r[1] + ',' + r[2] + ',' + r[3] + ',' + r[4] + ',' + r[5] + ',' + r[6] + ',' + r[7];
    let e = g.get(key);
    if (!e) { e = [r[1], r[2], r[3], r[4], r[5], r[6], r[7], 0, 0, 0]; g.set(key, e); }
    e[7] += r[8]; e[8] += r[9]; e[9] += r[10];
  }
  return Array.from(g.values());
}

/* ── Skeleton ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const DrilldownSkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="drilldown-skeleton">
    <div className="max-w-[1340px] mx-auto">
      <SB className="h-2 w-72 mb-2" delay={0} />
      <SB className="h-6 w-[560px] mb-1.5" delay={30} />
      <SB className="h-3 w-[620px] mb-4" delay={60} />
      <div className="flex gap-2.5">
        {[212, 212, 212, 300].map((w, i) => (
          <div key={i} className="rounded-2xl p-3 flex-none" style={{ width: w, height: 420, background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
            <SB className="h-2.5 w-16 mb-2" delay={100 + i * 40} />
            <SB className="h-3.5 w-24 mb-3" delay={120 + i * 40} />
            {Array.from({ length: 8 }, (_, j) => (
              <SB key={j} className="h-8 w-full mb-1.5" delay={140 + i * 40 + j * 20} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ── Dimension column (Country / Customer / Species / Process / Form / Grade) ── */
const DimColumn = ({ k, rows, sel, L, metric, onPick }) => {
  const items = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      let ok = true;
      for (let i = 0; i < k; i++) { if (r[i] !== sel[i]) { ok = false; break; } }
      if (!ok) continue;
      const key = r[k];
      let e = g.get(key);
      if (!e) { e = { v: 0, q: 0, n: 0 }; g.set(key, e); }
      e.v += r[COLV]; e.q += r[COLQ]; e.n++;
    }
    const arr = Array.from(g.entries()).map(([key, v]) => ({ key, name: L[DIMS[k]][key], v: v.v, q: v.q, n: v.n }));
    arr.sort((a, b) => metric === 'val' ? b.v - a.v : b.q - a.q);
    return arr;
  }, [rows, sel, k, L, metric]);

  const total = items.reduce((a, b) => a + (metric === 'val' ? b.v : b.q), 0) || 1;
  const max = items.length ? (metric === 'val' ? items[0].v : items[0].q) : 1;

  const subtitle = k === 0 ? 'Destination countries'
    : k === 1 ? L.country[sel[0]]
    : k === 2 ? L.customer[sel[1]]
    : k === 3 ? L.species[sel[2]]
    : k === 4 ? L.process[sel[3]]
    : L.form[sel[4]];

  return (
    <div className="flex-none w-[212px] bg-dash-panel border border-dash-line rounded-2xl flex flex-col max-h-[600px] overflow-hidden">
      <div className="px-3.5 pt-2.5 pb-2 border-b border-dash-line">
        <div className="font-mono text-[9.5px] tracking-[0.13em] uppercase" style={{ color: ACC[k] }}>{KLAB[k]}</div>
        <div className="font-grotesk text-[13px] font-bold text-dash-ink mt-0.5 truncate" title={subtitle}>{subtitle}</div>
        <div className="font-mono text-[10px] text-[#5C7980] mt-0.5">{items.length} items · {metric === 'val' ? fmtV(total) : fmtQ(total)}</div>
      </div>
      <div className="overflow-y-auto p-1.5 flex flex-col gap-1" data-testid={`dim-col-${k}`}>
        {items.map(it => {
          const mv = metric === 'val' ? it.v : it.q;
          const w = (mv / max * 100).toFixed(0);
          const pct = (mv / total * 100).toFixed(1);
          const isSel = sel[k] === it.key;
          return (
            <div
              key={it.key}
              onClick={() => onPick(k, it.key)}
              className={`relative px-2.5 py-2 rounded-lg cursor-pointer border transition ${isSel ? '' : 'border-transparent hover:bg-white/[0.04]'}`}
              style={isSel ? { background: 'rgba(79,195,201,0.12)', borderColor: ACC[k] } : undefined}
              data-testid={`dim-item-${k}-${it.key}`}
            >
              <span className="absolute right-1.5 top-2 text-[14px] leading-none" style={{ color: isSel ? ACC[k] : '#5C7980' }}>&rsaquo;</span>
              <div className="text-[12.5px] text-dash-ink truncate pr-3">{it.name}</div>
              <div className="flex justify-between items-baseline mt-0.5">
                <span className="font-mono text-[11px] font-bold" style={{ color: ACC[k] }}>{metric === 'val' ? fmtV(mv) : fmtQ(mv)}</span>
                <span className="font-mono text-[9.5px] text-[#5C7980]">{pct}%</span>
              </div>
              <div className="h-1 bg-dash-panel2 rounded mt-1.5 overflow-hidden">
                <div className="h-full rounded" style={{ width: `${w}%`, background: ACC[k] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Leaf column: Suppliers ── */
const SupplierColumn = ({ rows, sel, L, metric, selSup, onSelect }) => {
  const items = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      let ok = true;
      for (let i = 0; i < 6; i++) { if (r[i] !== sel[i]) { ok = false; break; } }
      if (!ok) continue;
      const key = r[6];
      let e = g.get(key);
      if (!e) { e = { v: 0, q: 0, n: 0 }; g.set(key, e); }
      e.v += r[COLV]; e.q += r[COLQ]; e.n++;
    }
    const arr = Array.from(g.entries()).map(([key, v]) => ({ key, name: L.supplier[key], v: v.v, q: v.q, price: v.q > 0 ? v.v / v.q : 0 }));
    arr.sort((a, b) => metric === 'val' ? b.v - a.v : b.q - a.q);
    return arr;
  }, [rows, sel, L, metric]);

  return (
    <div className="flex-none w-[300px] bg-dash-panel border border-dash-line rounded-2xl flex flex-col max-h-[600px] overflow-hidden">
      <div className="px-3.5 pt-2.5 pb-2 border-b border-dash-line">
        <div className="font-mono text-[9.5px] tracking-[0.13em] uppercase text-dash-amber">Suppliers</div>
        <div className="font-grotesk text-[13px] font-bold text-dash-ink mt-0.5 truncate">{L.grade[sel[5]]} · {L.form[sel[4]]}</div>
        <div className="font-mono text-[10px] text-[#5C7980] mt-0.5">{items.length} exporter(s) · click for price graph</div>
      </div>
      <div className="overflow-y-auto p-1.5 flex flex-col gap-1.5" data-testid="supplier-col">
        {items.map(it => (
          <div
            key={it.key}
            onClick={() => onSelect(it.key, it.name)}
            className={`px-2.5 py-2 rounded-lg border cursor-pointer transition ${selSup === it.key ? 'border-dash-amber bg-[rgba(242,177,76,0.12)]' : 'border-dash-line bg-white/[0.02] hover:border-dash-amber'}`}
            data-testid={`supplier-item-${it.key}`}
          >
            <div className="text-[12.5px] text-dash-ink truncate mb-1.5">{it.name}</div>
            <div className="grid grid-cols-3 gap-1">
              <div>
                <div className="font-mono text-[8.5px] tracking-wide uppercase text-[#5C7980]">Qty</div>
                <div className="font-mono text-xs font-bold text-dash-ink">{fmtQ(it.q)}</div>
              </div>
              <div>
                <div className="font-mono text-[8.5px] tracking-wide uppercase text-[#5C7980]">Price/kg</div>
                <div className="font-mono text-xs font-bold text-dash-amber">${it.price.toFixed(2)}</div>
              </div>
              <div>
                <div className="font-mono text-[8.5px] tracking-wide uppercase text-[#5C7980]">Value</div>
                <div className="font-mono text-xs font-bold text-dash-ice">{fmtV(it.v)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Price trend chart — always computed from the FULL, unfiltered-by-period
   history (fullRows), independent of the applied period filter. Matches the
   legacy dashboard's behaviour: the price trend is meant to show full context
   regardless of which month range is being navigated. ── */
const PriceTrendChart = ({ fullRows, sel, selSup, L }) => {
  const sp = sel[2], pr = sel[3], fo = sel[4], gr = sel[5];
  const nMonths = L.month?.length || 23;

  const chart = useMemo(() => {
    const srows = fullRows.filter(r => r[7] === selSup && r[3] === sp && r[4] === pr && r[5] === fo && r[6] === gr);
    const mrows = fullRows.filter(r => r[3] === sp && r[4] === pr && r[5] === fo && r[6] === gr);

    const sByM = new Map();
    for (const r of srows) { const m = r[0]; let e = sByM.get(m); if (!e) { e = { v: 0, q: 0 }; sByM.set(m, e); } e.v += r[9]; e.q += r[8]; }
    const pts = Array.from(sByM.entries()).map(([mi, v]) => ({ mi: +mi, price: v.q > 0 ? v.v / v.q : 0, qty: v.q })).sort((a, b) => a.mi - b.mi);

    const mByM = new Map();
    for (const r of mrows) { const m = r[0]; let e = mByM.get(m); if (!e) { e = { v: 0, q: 0 }; mByM.set(m, e); } e.v += r[9]; e.q += r[8]; }
    const mpts = Array.from(mByM.entries()).map(([mi, v]) => ({ mi: +mi, price: v.q > 0 ? v.v / v.q : 0 })).sort((a, b) => a.mi - b.mi);

    const sV = srows.reduce((a, r) => a + r[9], 0), sQ = srows.reduce((a, r) => a + r[8], 0);
    const savg = sQ > 0 ? sV / sQ : 0;
    const mV = mrows.reduce((a, r) => a + r[9], 0), mQ = mrows.reduce((a, r) => a + r[8], 0);
    const mAvg = mQ > 0 ? mV / mQ : 0;

    return { pts, mpts, savg, mAvg, sQ, srowsLen: srows.length };
  }, [fullRows, sp, pr, fo, gr, selSup]);

  const { pts, mpts, savg, mAvg, sQ, srowsLen } = chart;
  if (!pts.length) return null;

  const allP = pts.map(p => p.price).concat(mpts.map(p => p.price));
  let pmin = Math.min(...allP), pmax = Math.max(...allP);
  if (pmin === pmax) { pmin -= 1; pmax += 1; }
  const padp = (pmax - pmin) * 0.12;
  pmin = Math.max(0, pmin - padp); pmax += padp;

  const W = 920, H = 320, ML = 56, MR = 22, MT = 18, MB = 42, pw = W - ML - MR, ph = H - MT - MB;
  const x = (mi) => ML + mi / (nMonths - 1) * pw;
  const y = (v) => MT + (1 - (v - pmin) / (pmax - pmin)) * ph;

  const diff = mAvg > 0 ? (savg - mAvg) / mAvg * 100 : 0;
  const period = L.month ? `${L.month[0]} – ${L.month[L.month.length - 1]}` : 'All months';

  return (
    <div className="bg-dash-panel border border-dash-line rounded-2xl p-4 mt-3.5" data-testid="price-trend-chart">
      <h3 className="font-grotesk font-bold text-base">Price per kg — <span className="text-dash-amber">{L.supplier[selSup]}</span></h3>
      <div className="font-mono text-[11px] text-dash-mut my-1.5">
        {L.species[sp]} · {L.process[pr]} · {L.form[fo]} · {L.grade[gr]} · {period} · across all destinations ({srowsLen} shipment{srowsLen !== 1 ? 's' : ''})
      </div>
      <div className="flex gap-4.5 font-mono text-[11px] text-dash-mut mb-1.5">
        <span><i className="inline-block w-[15px] h-[3px] rounded-sm mr-1.5 align-middle" style={{ background: '#F2B14C' }} />This supplier ($/kg monthly avg)</span>
        <span><i className="inline-block w-[15px] h-[3px] rounded-sm mr-1.5 align-middle" style={{ background: '#4FC3C9' }} />Market avg, same product (monthly)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet">
        {[0, 1, 2, 3, 4].map(t => {
          const pv = pmin + (pmax - pmin) * t / 4, yy = y(pv);
          return (
            <g key={t}>
              <line x1={ML} y1={yy} x2={W - MR} y2={yy} stroke="#1E4049" strokeWidth={1} />
              <text x={ML - 8} y={yy + 3.5} textAnchor="end" fontSize={10} fill="#7E9AA0">${pv.toFixed(1)}</text>
            </g>
          );
        })}
        {Array.from({ length: nMonths }, (_, mi) => mi).map(mi => (
          <g key={mi}>
            {(mi % 3 === 0 || mi === nMonths - 1) && (
              <text x={x(mi)} y={H - MB + 16} textAnchor="middle" fontSize={9} fill="#7E9AA0">{(L.month?.[mi] || 'M' + mi).replace('-20', '\'')}</text>
            )}
            <line x1={x(mi)} y1={H - MB} x2={x(mi)} y2={H - MB + 4} stroke="#1E4049" strokeWidth={1} />
          </g>
        ))}
        {mpts.length > 1 && (
          <polyline points={mpts.map(p => `${x(p.mi)},${y(p.price)}`).join(' ')} fill="none" stroke="#4FC3C9" strokeWidth={1.6} strokeDasharray="5 4" opacity={0.7} />
        )}
        {pts.length > 1 && (
          <polyline points={pts.map(p => `${x(p.mi)},${y(p.price)}`).join(' ')} fill="none" stroke="#F2B14C" strokeWidth={2.4} />
        )}
        {pts.map(p => (
          <circle key={p.mi} cx={x(p.mi)} cy={y(p.price)} r={4} fill="#F2B14C" stroke="#081519" strokeWidth={1.5}>
            <title>{(L.month?.[p.mi] || 'M' + p.mi)}  ·  ${p.price.toFixed(2)}/kg  ·  {fmtQ(p.qty)}</title>
          </circle>
        ))}
      </svg>
      <div className="flex gap-5.5 flex-wrap mt-3 font-mono text-xs text-dash-mut">
        <span>Avg <b className="text-dash-ink">${savg.toFixed(2)}/kg</b></span>
        <span>Range <b className="text-dash-ink">${Math.min(...pts.map(p => p.price)).toFixed(2)} – ${Math.max(...pts.map(p => p.price)).toFixed(2)}</b></span>
        <span>Total qty <b className="text-dash-ink">{fmtQ(sQ)}</b></span>
        <span>vs market <b className={diff >= 0 ? 'text-dash-coral' : 'text-[#8FD694]'}>{diff >= 0 ? '+' : ''}{diff.toFixed(1)}%</b> (mkt ${mAvg.toFixed(2)})</span>
      </div>
    </div>
  );
};

/* ── Main Page ── */
export default function MarketDrilldownReact() {
  const [L, setL] = useState(null);
  const [fullRows, setFullRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [months, setMonths] = useState([]);
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [appliedRange, setAppliedRange] = useState([0, 0]);
  const [isFiltered, setIsFiltered] = useState(false);

  const [metric, setMetric] = useState('val');
  const [sel, setSel] = useState([]);
  const [selSup, setSelSup] = useState(null);

  const [shipmentsOpen, setShipmentsOpen] = useState(false);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipments, setShipments] = useState(null);

  const colsRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/static/drilldown_full.json`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        if (!d.L || !d.rows) throw new Error('Malformed drilldown data');
        setL(d.L);
        setFullRows(d.rows);
        const mm = d.L.month || [];
        setMonths(mm);
        setToIdx(Math.max(0, mm.length - 1));
        setAppliedRange([0, Math.max(0, mm.length - 1)]);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const rows = useMemo(() => {
    if (!fullRows) return [];
    return aggregateMonths(fullRows, appliedRange[0], appliedRange[1]);
  }, [fullRows, appliedRange]);

  useEffect(() => {
    const el = colsRef.current;
    if (el) requestAnimationFrame(() => { el.scrollLeft = el.scrollWidth; });
  }, [sel.length]);

  const applyPeriod = useCallback(() => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setAppliedRange([fromIdx, toIdx]);
    setIsFiltered(true);
    setSel([]); setSelSup(null); setShipmentsOpen(false);
  }, [fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    const last = Math.max(0, months.length - 1);
    setFromIdx(0); setToIdx(last);
    setAppliedRange([0, last]);
    setIsFiltered(false);
    setSel([]); setSelSup(null); setShipmentsOpen(false);
  }, [months]);

  const handlePick = useCallback((k, key) => {
    setSel(prev => [...prev.slice(0, k), key]);
    setSelSup(null);
    setShipmentsOpen(false);
  }, []);

  const handleReset = useCallback(() => {
    setSel([]); setSelSup(null); setShipmentsOpen(false);
  }, []);

  const handleSelectSupplier = useCallback((supKey, supName) => {
    setSelSup(supKey);
    setShipmentsOpen(true);
    setShipmentsLoading(true);
    setShipments(null);

    const params = new URLSearchParams();
    if (sel.length > 0) params.set('country', L.country[sel[0]]);
    if (sel.length > 1) params.set('customer', L.customer[sel[1]]);
    if (sel.length > 2) params.set('species', L.species[sel[2]]);
    if (sel.length > 3) params.set('process', L.process[sel[3]]);
    if (sel.length > 4) params.set('form', L.form[sel[4]]);
    if (sel.length > 5) params.set('grade', L.grade[sel[5]]);
    params.set('supplier', supName);
    if (isFiltered) {
      params.set('months', months.slice(appliedRange[0], appliedRange[1] + 1).join(','));
    }

    fetch(`${API}/api/dashboard-data/shipments?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setShipments(d); setShipmentsLoading(false); })
      .catch(() => { setShipments(null); setShipmentsLoading(false); });
  }, [sel, L, isFiltered, months, appliedRange]);

  if (loading) return <DrilldownSkeleton />;
  if (error) return <div className="h-full bg-dash-bg flex items-center justify-center"><div className="text-dash-coral font-mono text-sm">Error: {error}</div></div>;

  const periodLabel = isFiltered
    ? `${months[appliedRange[0]]} to ${months[appliedRange[1]]} (${appliedRange[1] - appliedRange[0] + 1} month${appliedRange[1] - appliedRange[0] === 0 ? '' : 's'})`
    : `All ${months.length || 23} months · ${months[0]} to ${months[months.length - 1]}`;

  const pathLabels = sel.map((key, i) => L[DIMS[i]][key]);
  const columnCount = sel.length + 1; // k=0..sel.length; k===6 renders the leaf supplier column

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="market-drilldown-react">
      <div className="max-w-[1340px] mx-auto">

        {/* Header */}
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-dash-ice mb-2">
          Drill-Down Explorer &middot; Column View &middot; Apr 2024 – Feb 2026
        </div>
        <h1 className="font-grotesk font-extrabold text-2xl tracking-tight mb-1">
          Country &rarr; Customer &rarr; Species &rarr; Process &rarr; Form &rarr; Grade &rarr; <span className="text-dash-ice">Suppliers</span>
        </h1>
        <p className="text-dash-mut text-[13px] mb-4 max-w-[840px]">
          Click through the columns left to right. Each pick opens the next column; the last shows the exporters who supplied that exact product — with quantity and price.
        </p>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="mb-4">
            <PeriodFilter
              months={months}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
            />
          </div>
        )}

        {/* Breadcrumb bar */}
        <div className="flex items-center gap-3 flex-wrap mb-1" data-testid="breadcrumb-bar">
          <div className="flex-1 font-mono text-xs text-dash-mut min-h-[18px]" data-testid="path">
            {sel.length === 0 ? (
              <span className="text-[#5C7980]">Pick a country to begin &rarr;</span>
            ) : (
              <>
                {pathLabels.map((lab, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-[#5C7980] mx-1.5">&rarr;</span>}
                    <b className="text-dash-ink">{lab}</b>
                  </React.Fragment>
                ))}
                {sel.length === 6 && (
                  <>
                    <span className="text-[#5C7980] mx-1.5">&rarr;</span>
                    <b className="text-dash-amber">Suppliers</b>
                  </>
                )}
              </>
            )}
          </div>
          <button
            onClick={handleReset}
            className="font-mono text-[11px] text-dash-ice bg-transparent border border-dash-line rounded-lg px-2.5 py-1.5 cursor-pointer hover:border-dash-ice transition"
            data-testid="drilldown-reset"
          >
            &#10227; Reset
          </button>
          <div className="flex bg-dash-panel border border-dash-line rounded-lg p-0.5 gap-0.5" data-testid="metric-toggle">
            <button
              onClick={() => setMetric('val')}
              className={`font-mono text-[11px] px-3.5 py-1.5 rounded-md transition ${metric === 'val' ? 'bg-dash-ice text-[#062023] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
            >
              By Value $
            </button>
            <button
              onClick={() => setMetric('qty')}
              className={`font-mono text-[11px] px-3.5 py-1.5 rounded-md transition ${metric === 'qty' ? 'bg-dash-ice text-[#062023] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
            >
              By Volume
            </button>
          </div>
        </div>

        {/* Columns */}
        <div ref={colsRef} className="flex gap-2.5 overflow-x-auto pt-3.5 pb-4" data-testid="cols">
          {Array.from({ length: columnCount }, (_, k) => k).map(k => (
            k < 6
              ? <DimColumn key={k} k={k} rows={rows} sel={sel} L={L} metric={metric} onPick={handlePick} />
              : <SupplierColumn key="leaf" rows={rows} sel={sel} L={L} metric={metric} selSup={selSup} onSelect={handleSelectSupplier} />
          ))}
        </div>

        {/* Price trend chart */}
        {sel.length === 6 && selSup !== null && (
          <PriceTrendChart fullRows={fullRows} sel={sel} selSup={selSup} L={L} />
        )}

        {/* Shipment details */}
        {shipmentsOpen && (
          <div className="bg-[rgba(12,32,41,.95)] border border-[rgba(79,195,201,0.2)] rounded-2xl p-5 mt-3.5" data-testid="shipment-panel">
            <div className="flex justify-between items-center mb-3.5">
              <h3 className="text-[15px] font-bold text-dash-ink">Shipment Details — <span className="text-dash-coral">{L.supplier[selSup]}</span></h3>
              <button
                onClick={() => setShipmentsOpen(false)}
                className="text-xs text-dash-mut border border-[rgba(79,195,201,0.2)] rounded-md px-3 py-1 hover:bg-[rgba(79,195,201,0.1)] hover:text-dash-ink transition"
                data-testid="shipment-close"
              >
                Close ✕
              </button>
            </div>
            {shipmentsLoading ? (
              <div className="text-center py-8 text-dash-mut">Loading shipments...</div>
            ) : !shipments ? (
              <div className="text-center py-8 text-dash-coral">Error loading shipments</div>
            ) : (
              <>
                <div className="flex gap-5 mb-3.5 flex-wrap">
                  <div className="bg-[rgba(16,42,52,0.8)] rounded-lg px-4 py-2.5">
                    <div className="text-[10px] tracking-[0.1em] uppercase text-dash-mut mb-0.5">Shipments</div>
                    <div className="text-lg font-extrabold text-dash-ink">{shipments.total}</div>
                  </div>
                  <div className="bg-[rgba(16,42,52,0.8)] rounded-lg px-4 py-2.5">
                    <div className="text-[10px] tracking-[0.1em] uppercase text-dash-mut mb-0.5">Total Value</div>
                    <div className="text-lg font-extrabold text-dash-coral">{fmtShipVal(shipments.total_val)}</div>
                  </div>
                  <div className="bg-[rgba(16,42,52,0.8)] rounded-lg px-4 py-2.5">
                    <div className="text-[10px] tracking-[0.1em] uppercase text-dash-mut mb-0.5">Total Qty</div>
                    <div className="text-lg font-extrabold text-dash-ice">{(shipments.total_qty / 1000).toFixed(1)} t</div>
                  </div>
                  <div className="bg-[rgba(16,42,52,0.8)] rounded-lg px-4 py-2.5">
                    <div className="text-[10px] tracking-[0.1em] uppercase text-dash-mut mb-0.5">Avg Price</div>
                    <div className="text-lg font-extrabold text-dash-amber">${shipments.avg_price}/kg</div>
                  </div>
                  {shipments.total > shipments.shown && (
                    <div className="bg-[rgba(16,42,52,0.8)] rounded-lg px-4 py-2.5">
                      <div className="text-[10px] tracking-[0.1em] uppercase text-dash-mut mb-0.5">Showing</div>
                      <div className="text-lg font-extrabold text-dash-ink">{shipments.shown} of {shipments.total}</div>
                    </div>
                  )}
                </div>
                {!shipments.shipments?.length ? (
                  <div className="text-center py-8 text-dash-mut">No shipment records found</div>
                ) : (
                  <div className="max-h-[360px] overflow-y-auto rounded-lg border border-[rgba(79,195,201,0.1)]">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="sticky top-0 bg-[rgba(16,42,52,0.9)]">
                          <th className="text-left px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Date</th>
                          <th className="text-left px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Month</th>
                          <th className="text-left px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Dest Port</th>
                          <th className="text-left px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Origin Port</th>
                          <th className="text-left px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Description</th>
                          <th className="text-right px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">Qty (KG)</th>
                          <th className="text-right px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">$/KG</th>
                          <th className="text-right px-2.5 py-2 text-dash-ice text-[10px] tracking-wider uppercase">FOB Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shipments.shipments.map((s, i) => (
                          <tr key={i} className="border-b border-[rgba(79,195,201,0.06)] hover:bg-[rgba(79,195,201,0.04)]">
                            <td className="px-2.5 py-1.5 text-[#c0d4d8]">{s.date || '-'}</td>
                            <td className="px-2.5 py-1.5 text-[#c0d4d8]">{s.month || '-'}</td>
                            <td className="px-2.5 py-1.5 text-dash-amber">{s.port_dest || '-'}</td>
                            <td className="px-2.5 py-1.5 text-[#c0d4d8]">{s.port_origin || '-'}</td>
                            <td className="px-2.5 py-1.5 text-[#c0d4d8]">{s.desc || '-'}</td>
                            <td className="px-2.5 py-1.5 text-right font-mono text-dash-ink">{s.qty.toLocaleString()}</td>
                            <td className="px-2.5 py-1.5 text-right font-mono text-dash-ink">${s.rate.toFixed(2)}</td>
                            <td className="px-2.5 py-1.5 text-right font-mono text-dash-ink">${s.val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Hint */}
        <div className="mt-6 border-t border-dash-line pt-3.5 text-[#5C7980] text-[11.5px] leading-relaxed">
          Columns cascade left&rarr;right: Country · Customer · Species · Process · Form · Grade · Suppliers. Click any earlier column to re-pick from there. Value/Volume re-ranks every column.
        </div>

        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
